<script setup>
import { ref, onMounted, defineProps, defineEmits } from 'vue';
import Data from '@/api/data';
import SetupCalculation from '@/components/modals/SetupCalculation.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  dataID: {
    type: String,
    required: true
  }
});

const emit = defineEmits(['update:modelValue']);

// Local state
const config = ref({ ...props.modelValue });
const loadingData = ref(false);
const subcollections = ref({});
const validFields = ref([]);
const showCalcModal = ref(false);

// Lifecycle
onMounted(async () => {
  await getSubcollections();
  
  if (config.value.source) {
    updateValidFields(config.value.source);

    if (!config.value.columns || config.value.columns.length === 0) {
      config.value.columns = [{ field: '', label: '' }];
      emitUpdate();
    }
  }
});

// API Methods
const getSubcollections = async () => {
  loadingData.value = true;
  try {
    const response = await Data.get(props.dataID);
    if (response.body.status === 'success') {
      const ignoreSubcollections = ['Net_Cfg', 'action_request', 'action_result', 'location', 'messages'];
      subcollections.value = Object.fromEntries(
        Object.entries(response.body.details)
          .filter(([key]) => !ignoreSubcollections.includes(key))
          .sort(([a], [b]) => a.localeCompare(b))
      );
    }
  } catch (err) {
    console.error('Error fetching subcollections:', err);
  } finally {
    loadingData.value = false;
  }
};

const updateValidFields = (source) => {
  if (source && subcollections.value[source]) {
    validFields.value = Object.keys(subcollections.value[source]).sort((a, b) => a.localeCompare(b));
  } else {
    validFields.value = [];
  }
};

const handleSourceChange = (newSource) => {
  if (newSource === '__new_source__') {
    showCalcModal.value = true;
    return;
  }
  config.value.source = newSource;
  updateValidFields(newSource);
  
  config.value.columns = [{ field: '', label: '' }];
  
  emitUpdate();
};

const addColumn = () => {
  if (!config.value.columns) {
    config.value.columns = [];
  }
  
  config.value.columns.push({
    field: '',
    label: ''
  });
  
  emitUpdate();
};

const removeColumn = (index) => {
  config.value.columns.splice(index, 1);
  emitUpdate();
};

const emitUpdate = () => {
  emit('update:modelValue', config.value);
};

const handleNewSource = ({ name }) => {
  showCalcModal.value = false;
  getSubcollections().then(() => {
    handleSourceChange(name.replace(/\s+/g, '_'));
  });
};

</script>

<template>
  <div class="space-y-4">
    <!-- Data Source -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Data Source *
        <span v-if="loadingData" class="inline-flex items-center ml-2" title="Loading options...">
          <span class="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
        </span>
      </label>
      <select
        :value="config.source"
        @change="handleSourceChange($event.target.value)"
        :disabled="loadingData"
        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <option value="__new_source__">+ New Source</option>
        <option value="">Select a source...</option>
        <option v-for="source in Object.keys(subcollections)" :key="source" :value="source">
          {{ source }}
        </option>
      </select>
    </div>

    <!-- Number of Rows -->
    <div v-if="config.source">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Number of Rows
        </label>
        <input
          v-model.number="config.rowLimit"
          type="number"
          min="1"
          max="100"
          placeholder="10"
          @input="emitUpdate"
          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Number of most recent records to display (1-100)</p>
      </div>

      <!-- Columns Section -->
      <div v-if="config.source">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Columns to Display *
        </label>

        <!-- Column Configurations -->
        <div v-for="(column, index) in config.columns ?? []" :key="index" class="p-4 border border-gray-300 dark:border-gray-600 rounded-lg mb-3 space-y-3">
          <div class="flex justify-between items-start">
            <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300">Column {{ index + 1 }}</h4>
            <button
              @click="removeColumn(index)"
              type="button"
              class="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              Remove
            </button>
          </div>

          <!-- Field Selection -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Field Name *
            </label>
            <select
              v-model="column.field"
              @change="emitUpdate"
              :disabled="loadingData"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Select a field...</option>
              <option v-for="field in validFields" :key="field" :value="field">
                {{ field }}
              </option>
            </select>
          </div>

          <!-- Column Label -->
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Column Header Label
            </label>
            <input
              v-model="column.label"
              type="text"
              placeholder="e.g., Temperature"
              @input="emitUpdate"
              class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Leave blank to use field name</p>
          </div>
        </div>

        <button
          @click="addColumn"
          type="button"
          class="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          + Add Column
        </button>
      </div>
  </div>

  <SetupCalculation
    v-if="showCalcModal"
    :dataID="dataID"
    :subcollectionOptions="subcollections"
    @calculation-details="handleNewSource"
    @close="showCalcModal = false"
  />
</template>
